import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const AbsenceManagement = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [academicYears, setAcademicYears] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const [filters, setFilters] = useState({
    academicYear: '',
    department: '',
    class: '',
    course: '',
    semester: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (filters.department && filters.academicYear) {
      fetchClasses();
    }
  }, [filters.department, filters.academicYear]);

  useEffect(() => {
    if (filters.department) {
      fetchCourses();
    }
  }, [filters.department]);

  useEffect(() => {
    if (filters.class || filters.course) {
      fetchAbsences();
    }
  }, [filters.class, filters.course, filters.semester]);

  const fetchInitialData = async () => {
    try {
      const [yearRes, deptRes] = await Promise.all([
        axios.get(`${API_URL}/api/academic-years`),
        axios.get(`${API_URL}/api/departments`)
      ]);
      setAcademicYears(yearRes.data);
      setDepartments(deptRes.data);
      
      const current = yearRes.data.find(y => y.isCurrent);
      if (current) {
        setFilters(prev => ({ ...prev, academicYear: current._id }));
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
      showError('Erreur lors du chargement des données');
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes`);
      const filtered = response.data.filter(
        cls => cls.department._id === filters.department && 
               cls.academicYear._id === filters.academicYear
      );
      setClasses(filtered);
    } catch (error) {
      console.error('Erreur chargement classes:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses?department=${filters.department}`);
      setCourses(response.data);
    } catch (error) {
      console.error('Erreur chargement cours:', error);
    }
  };

  const fetchAbsences = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.academicYear) params.append('academicYear', filters.academicYear);
      if (filters.class) params.append('class', filters.class);
      if (filters.course) params.append('course', filters.course);
      if (filters.semester) params.append('semester', filters.semester);
      params.append('_t', Date.now().toString());

      const response = await axios.get(`${API_URL}/api/absences?${params.toString()}`);
      setAbsences(response.data);
      calculateStats(response.data);
    } catch (error) {
      console.error('Erreur chargement absences:', error);
      showError('Erreur lors du chargement des absences');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (absencesData) => {
    const total = absencesData.length;
    const justified = absencesData.filter(a => a.isJustified).length;
    const unjustified = total - justified;
    
    const byStudent = absencesData.reduce((acc, absence) => {
      const studentId = absence.student._id;
      if (!acc[studentId]) {
        acc[studentId] = {
          student: absence.student,
          total: 0,
          justified: 0,
          unjustified: 0
        };
      }
      acc[studentId].total++;
      if (absence.isJustified) {
        acc[studentId].justified++;
      } else {
        acc[studentId].unjustified++;
      }
      return acc;
    }, {});

    setStats({
      total,
      justified,
      unjustified,
      byStudent: Object.values(byStudent)
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    
    if (name === 'department' || name === 'academicYear') {
      setFilters(prev => ({ ...prev, class: '', course: '' }));
      setAbsences([]);
      setStats(null);
    }
  };

  const handleJustifyAbsence = async (absenceId) => {
    try {
      await axios.put(`${API_URL}/api/absences/${absenceId}`, { isJustified: true });
      showSuccess('Absence justifiée avec succès');
      fetchAbsences();
    } catch (error) {
      console.error('Erreur justification:', error);
      showError('Erreur lors de la justification');
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette absence ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/absences/${absenceId}`);
      showSuccess('Absence supprimée avec succès');
      fetchAbsences();
    } catch (error) {
      console.error('Erreur suppression:', error);
      showError('Erreur lors de la suppression');
    }
  };

  const selectedClass = classes.find(c => c._id === filters.class);
  const selectedCourse = courses.find(c => c._id === filters.course);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">📊 Gestion des Absences</h1>
          <p className="text-gray-600">Consulter et gérer les absences des étudiants</p>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Année universitaire <span className="text-red-500">*</span>
              </label>
              <select
                name="academicYear"
                value={filters.academicYear}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Département <span className="text-red-500">*</span>
              </label>
              <select
                name="department"
                value={filters.department}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
              <select
                name="class"
                value={filters.class}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                disabled={!filters.department || !filters.academicYear}
              >
                <option value="">Toutes les classes</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Matière</label>
              <select
                name="course"
                value={filters.course}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                disabled={!filters.department}
              >
                <option value="">Toutes les matières</option>
                {courses.map(course => (
                  <option key={course._id} value={course._id}>{course.code} - {course.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
              <select
                name="semester"
                value={filters.semester}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Tous les semestres</option>
                <option value="1">Semestre 1</option>
                <option value="2">Semestre 2</option>
              </select>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm text-gray-600 mb-1">Total des absences</div>
              <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm text-gray-600 mb-1">Absences justifiées</div>
              <div className="text-3xl font-bold text-green-600">{stats.justified}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm text-gray-600 mb-1">Absences non justifiées</div>
              <div className="text-3xl font-bold text-red-600">{stats.unjustified}</div>
            </div>
          </div>
        )}

        {/* Liste des absences */}
        {(filters.class || filters.course) && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedClass?.name || selectedCourse?.name || 'Absences'} - {absences.length} absence(s)
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : absences.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">✅</div>
                <p>Aucune absence enregistrée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Étudiant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classe</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matière</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Semestre</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {absences.map(absence => (
                      <tr key={absence._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {new Date(absence.date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {absence.student?.firstName} {absence.student?.lastName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{absence.class?.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {absence.course?.code} - {absence.course?.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">S{absence.semester}</td>
                        <td className="px-6 py-4 text-sm">
                          {absence.isJustified ? (
                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                              Justifiée
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                              Non justifiée
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          {!absence.isJustified && (
                            <button
                              onClick={() => handleJustifyAbsence(absence._id)}
                              className="text-green-600 hover:text-green-900 mr-3"
                            >
                              ✓ Justifier
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteAbsence(absence._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            🗑️ Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Statistiques par étudiant */}
        {stats && stats.byStudent.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm mt-6">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Statistiques par étudiant</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Étudiant</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Justifiées</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Non justifiées</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.byStudent
                    .sort((a, b) => b.unjustified - a.unjustified)
                    .map(stat => (
                      <tr key={stat.student._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {stat.student.firstName} {stat.student.lastName}
                        </td>
                        <td className="px-6 py-4 text-sm text-center text-gray-900">{stat.total}</td>
                        <td className="px-6 py-4 text-sm text-center text-green-600">{stat.justified}</td>
                        <td className="px-6 py-4 text-sm text-center">
                          <span className={stat.unjustified >= 3 ? 'text-red-600 font-bold' : 'text-gray-900'}>
                            {stat.unjustified}
                            {stat.unjustified >= 3 && ' ⚠️'}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AbsenceManagement;
